import { useCallback, useState } from "react";
import { useUpdateLeagueRoundMutation } from "../../api/endpoints/rounds";
import type {
  Clubhouse_LeagueRoundV2Dto,
  Clubhouse_RoundSetting,
} from "../../types/leagues";
import { toApplySettingsPatchBody } from "./roundSettingsPropagation";

export interface ApplyRoundSettingsResult {
  applied: number;
  failed: number;
}

export interface ApplyRoundSettingsArgs {
  facilityId: string;
  leagueId: string;
  /** Rounds to copy the settings into (already filtered to eligible targets). */
  targets: Clubhouse_LeagueRoundV2Dto[];
  /** The just-edited round's settings, the source of the propagated fields. */
  sourceSettings: Clubhouse_RoundSetting;
  /** Facility course list, used to resolve each target's slugged course id. */
  courses?: { id: number; displayName: string }[];
}

/**
 * Copies the propagated settings (conditions/scoring/automation) from a
 * just-edited round onto a set of target rounds via one PATCH each. Each target
 * keeps its own play window, course, and holes. Runs the PATCHes concurrently
 * and reports how many succeeded so the caller can show a partial-failure note.
 */
export function useApplyRoundSettings() {
  const [updateRound] = useUpdateLeagueRoundMutation();
  // Batch-level in-flight flag: the RTK mutation's own isLoading tracks only the
  // latest trigger, so with concurrent PATCHes it can flip false while the batch
  // is still settling. Track the whole Promise.allSettled here instead.
  const [isApplying, setIsApplying] = useState(false);

  const apply = useCallback(
    async ({
      facilityId,
      leagueId,
      targets,
      sourceSettings,
      courses,
    }: ApplyRoundSettingsArgs): Promise<ApplyRoundSettingsResult> => {
      setIsApplying(true);
      try {
        const results = await Promise.allSettled(
          targets.map((target) =>
            updateRound({
              facilityId,
              leagueId,
              roundId: String(target.id),
              body: toApplySettingsPatchBody(target, sourceSettings, courses),
            }).unwrap(),
          ),
        );
        const applied = results.filter((r) => r.status === "fulfilled").length;
        return { applied, failed: results.length - applied };
      } finally {
        setIsApplying(false);
      }
    },
    [updateRound],
  );

  return { apply, isApplying };
}
